import { flattenedRowKey } from "@/domains/documents/utils/flattenDocumentRows";
import { useMeasuredVirtualRows } from "./useMeasuredVirtualRows";
import { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableRows } from "./useTableRows";
import type { useTableSizing } from "./useTableSizing";
type Options = Pick<ReturnType<typeof useTableRows>, "flattenedItems"> &
  Pick<ReturnType<typeof useTableSizing>, "scrollContainerRef"> &
  Pick<ReturnType<typeof documentTableOptions>, "hasMore" | "isLoadingMore" | "onLoadMore">;
export function useTableVirtualRows(options: Options) {
  const { flattenedItems, scrollContainerRef, hasMore, isLoadingMore, onLoadMore } = options;
  // Virtual scrolling setup - only render visible rows for performance.
  // Row heights are measured, so the estimates below only decide the total size
  // of rows not yet rendered — but a folder-heavy root is mostly unrendered
  // folder rows, and one estimate for every kind inflates the scroll height
  // there. Folder rows are a single line (`py-2.5`), container rows a taller
  // `py-4`, document rows grow with their tag chips.
  const virtual = useMeasuredVirtualRows({
    count: flattenedItems.length,
    getScrollElement: () => scrollContainerRef.current,
    // Measured heights are cached by this key. Left as the default (the index)
    // they follow the slot, not the row: the folder query resolves after the
    // documents do, every index shifts by the number of folder rows, and each
    // row is laid out at a height measured for whatever used to sit there.
    getItemKey: (index) => {
      const row = flattenedItems[index];
      return row ? flattenedRowKey(row) : index;
    },
    estimateSize: (index) => {
      const kind = flattenedItems[index]?.kind;
      if (kind === "folder") return 56;
      if (kind === "container") return 72;
      if (kind === "folder-version") return 44;
      return 80;
    },
    overscan: 5, // Render 5 extra rows above/below viewport
    measureElement: (element) => element?.getBoundingClientRect().height ?? 80,
  });

  // Track if initial render has completed (to avoid triggering on mount)
  const hasInitialRenderRef = useRef(false);

  // Intersection Observer for infinite scroll sentinel
  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    rootMargin: "100px", // Trigger 100px before visible
  });

  // Trigger loading more documents when sentinel comes into view
  useEffect(() => {
    // Skip initial render to avoid triggering on mount
    if (!hasInitialRenderRef.current) {
      hasInitialRenderRef.current = true;
      return;
    }

    if (inView && hasMore && !isLoadingMore && onLoadMore) {
      onLoadMore();
    }
  }, [inView, hasMore, isLoadingMore, onLoadMore]);
  return { ...virtual, sentinelRef };
}
